/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Button, Box, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import FieldsetForm from '../shared/ui/FieldsetForm'
import ResetPasswordFields from '../../components/forms/reset-password-fields'

const ResetPasswordForm = ({
    submitForm,
    clickSignIn = noop,
    form,
    prefix,
    isFirstInputFocus = false
}) => {
    const {formatMessage} = useIntl()

    return (
        <Stack spacing={24}>
            <Text variant="bodyLarge2">
                <FormattedMessage
                    defaultMessage="Enter your email and we'll send you instructions with <b>token</b> on how to reset your password to your email address"
                    id="reset_password_form.message.enter_your_email"
                    values={{
                        b: (chunks) => <b>{chunks}</b>
                    }}
                />
            </Text>

            <FieldsetForm
                legendText={formatMessage({
                    defaultMessage: 'Request to Reset Your Password',
                    id: 'reset_password_form.form.legend'
                })}
                data-testid="sf-auth-modal-form"
                onSubmit={form.handleSubmit(submitForm)}
                error={form.formState.errors?.global?.message}
            >
                <ResetPasswordFields
                    form={form}
                    prefix={prefix ? prefix : undefined}
                    isFirstInputFocus={isFirstInputFocus}
                />
                <Stack spacing={24}>
                    <Button
                        type="submit"
                        onClick={() => form.clearErrors('global')}
                        isLoading={form.formState.isSubmitting}
                        variant="primary"
                    >
                        <FormattedMessage
                            defaultMessage="Reset Password"
                            id="reset_password_form.button.reset_password"
                        />
                    </Button>

                    <Box textAlign="center">
                        <Button variant="link" onClick={clickSignIn}>
                            <FormattedMessage
                                defaultMessage="Return to Sign In"
                                id="reset_password_form.action.sign_in"
                            />
                        </Button>
                    </Box>
                </Stack>
            </FieldsetForm>
        </Stack>
    )
}

ResetPasswordForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    prefix: PropTypes.string,
    isFirstInputFocus: PropTypes.bool
}

export default ResetPasswordForm
